import React, { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LassieContext } from "../../../context/LassieProvider";
import { toStandardWplTimestamp, toZonedDateStr } from "../../../prettyDate";
import { useSyncSelectedWindfarm } from "../../../hooks/useSyncSelectedWindfarm";
import { useWindfarmTimeZone } from "../../../hooks/useWindfarmTimeZone";
import WindfarmTimeNote from "../../../components/windfarm-time-note/WindfarmTimeNote";
import OlMap from "../../../components/ol-map/OlMap";
import InfoCards from "../../../components/info-card/InfoCard";
import { getIconForTurbine } from "../../../components/ol-map/icons";
import WplButton from "../../../components/wpl-button/WplButton";
import TurbineLightningRuler from "../../../components/turbine-lightning-ruler/TurbineLightningRuler";
import WplInput from "../../../components/wpl-input/WplInput";
import TurbineAnimation from "../../../components/turbine-animation/TurbineAnimation";
import DataAvailableSince from "../../../components/data-available-since/DataAvailableSince";
import icon from "../../../assets/img/icon-info.png";
import arrowIcon from "../../../assets/img/arr_down.png";
import "./lightning-report.css";

export default function LightningLogbook() {
  const navigate = useNavigate();

  const {
    windfarms,
    fetchScenarios,
    scenarios,
    fetchNotifications,
    notifications,
    setNotifications,
    fetchHistoricActivity,
    historicActivity,
    setHistoricActivity,
    fetchHistoricUserAction,
    historicUserAction,
    setHistoricUserAction,
    fetchHistoricTriggers,
    historicTriggers,
    setHistoricTriggers,
    refetchUserActionsFlag
  } = useContext(LassieContext);

  const [selectedWindfarm, setSelectedWindfarm] = useState(null);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(false);

  let { windfarm_id, year, date } = useParams();
  if (windfarm_id) {
    windfarm_id = parseInt(windfarm_id);
  }
  const selectedYear = year ? parseInt(year, 10) : null;

  // Lightning dates and times are on the wind farm's clock, not the viewer's. Team action
  // dates are not: they are days someone picked, and are shown as they were entered.
  const timeZone = useWindfarmTimeZone(windfarm_id);

  // The year and the notification day live in the URL (.../year/2026/notification/2026-08-30),
  // so a link, a reload or the back button lands on the same notification.
  // Only resolved once the notifications belong to the windfarm in the URL.
  const selectedNotification = useMemo(() => {
    if (!notifications || !date || !selectedYear) return null;
    if (!selectedWindfarm || selectedWindfarm.id !== windfarm_id) return null;
    return (notifications[selectedYear] || []).find((n) => n.date === date) || null;
  }, [notifications, selectedYear, date, selectedWindfarm, windfarm_id]);

  const yearNotifications = (notifications && notifications[selectedYear]) || [];

  useEffect(() => {
    if (!windfarm_id || !windfarms) return;
    if (selectedWindfarm && selectedWindfarm.id === windfarm_id) return;

    setLoading(true);
    setSelectedWindfarm(windfarms.find((wf) => wf.id === windfarm_id));
    setNotifications(null);
    setHistoricActivity(null);
    setHistoricUserAction(null);
    setHistoricTriggers(null);
    setLoading(false);
  }, [selectedWindfarm, windfarm_id, windfarms]);

  useEffect(() => {
    if (!windfarm_id) return;

    const fetchData = async () => {
      setYears([]);
      setLoading(true);
      await fetchScenarios(windfarm_id);
      await fetchNotifications(windfarm_id);
      setLoading(false);
    };

    fetchData();
  }, [windfarm_id]);

  useEffect(() => {
    if (!selectedNotification || !windfarm_id) return;

    const fetchHistoricData = async () => {
      setLoading(true);
      const lastTimestamp = new Date(
        selectedNotification.last_lightning_timestamp
      );
      const from_timestamp = toStandardWplTimestamp(
        new Date(selectedNotification.first_lightning_timestamp)
      );
      const to_timestamp = toStandardWplTimestamp(lastTimestamp);
      const three_weeks_range_timestamp = toStandardWplTimestamp(
        new Date(lastTimestamp.setDate(lastTimestamp.getDate() + 21))
      );

      await fetchHistoricActivity(windfarm_id, from_timestamp, to_timestamp);
      await fetchHistoricUserAction(
        windfarm_id,
        from_timestamp,
        three_weeks_range_timestamp
      );
      await fetchHistoricTriggers(windfarm_id, from_timestamp, to_timestamp);
      setLoading(false);
    };

    fetchHistoricData();
  }, [selectedNotification?.date, selectedNotification?.first_lightning_timestamp, windfarm_id]);

  // The map popup can schedule or resolve inspections; reload the team response when it does.
  useEffect(() => {
    if (refetchUserActionsFlag === 0 || !selectedNotification || !windfarm_id) return;
    fetchHistoricUserAction(windfarm_id);
  }, [refetchUserActionsFlag]);

  useEffect(() => {
    if (!notifications) return;
    const extractedYears = Object.keys(notifications)
      .map((year) => parseInt(year, 10))
      .sort((a, b) => a - b);

    setYears(extractedYears);
  }, [notifications]);

  const MS_IN_DAY = 1000 * 60 * 60 * 24;
  const threeWeeksInMs = 21 * MS_IN_DAY;

  const latestDate = selectedNotification
    ? new Date(selectedNotification.last_lightning_timestamp)
    : null;

  const activeTurbineIds = new Set(
    historicActivity?.filter((turbine) => turbine.turbine_lightning.length > 0).map((turbine) => turbine.id)
  ) || [];

  const filteredActions =
    historicUserAction?.filter((hs) => {
      if (!latestDate) return false;
      const targetDate = new Date(hs.target_date);
      const isRelevantTurbine = activeTurbineIds.has(hs.turbine_id);
      return (
        isRelevantTurbine &&
        targetDate > latestDate &&
        targetDate <= new Date(latestDate.getTime() + threeWeeksInMs)
      );
    }) || [];

  const handleNameClick = (turbine) => {
    window.open(
      `/lassie/windfarm/${turbine.windfarm_id}/turbine/${turbine.id}`,
      "_blank"
    );
  };


  useSyncSelectedWindfarm(windfarm_id);

  return (
    <div className="lightning-report">
      <div className="logbook-heading">
        <h1>Logbook</h1>
        {selectedWindfarm && (
          <DataAvailableSince earliestData={selectedWindfarm.earliest_data} />
        )}
      </div>
      {loading && <TurbineAnimation />}
      <div className="windfarm-input-selector">
        <div className="lightning-report-select">
          <WplInput
            title="Select a windfarm"
            className="no-risk-windfarms"
            options={windfarms.map((wf) => wf.name)}
            onlySelectableOptions
            value={selectedWindfarm ? selectedWindfarm.name : ""}
            placeholder={"Select a windfarm..."}
            onChanged={(name) => {
              const wf = windfarms.find((w) => w.name === name);
              if (wf) {
                navigate(`/lassie/logbook/windfarm/${wf.id}`);
              }
            }}
          />
        </div>
        {/* Outside the select's narrow column, so the sentence keeps to one line. */}
        {selectedWindfarm && <WindfarmTimeNote timeZone={timeZone} />}

        <div className="from-to-date-wrapper">
          {years.map((year) => {
            return (
              <WplButton
                className={`year-option ${selectedYear === year ? "selected" : ""
                  }`}
                value={year}
                key={year}
                onClick={() => navigate(`/lassie/logbook/windfarm/${windfarm_id}/year/${year}`)}
              />
            );
          })}
        </div>
      </div>

      {/* Waits for the time zone too, so the dates do not first show in UTC and then shift. */}
      {!loading && notifications && selectedYear && timeZone !== undefined && (
        <>
          <div className="windfarm-input-selector">
            <div className="notications-info-area">
              <h3 className="notifications-label">
                {`There ${yearNotifications.length === 1 ? "was" : "were"} ${yearNotifications.length} notification${yearNotifications.length === 1 ? "" : "s"} in ${selectedYear}.`}
                <span className="info-tooltip-icon">
                  <img src={icon} alt="info-tooltip" />
                  <p className="info-container">
                    Each notification suggests serious lightning activity close
                    to at least one turbine. In the following section, you will
                    see an overview of what happened and what the response was.
                  </p>
                </span>
              </h3>
            </div>

            <div className="notification-selection">
              {notifications &&
                selectedYear ?
                yearNotifications.map((notif, i) => (
                  <div
                    key={i}
                    className={`selectable-notification ${selectedNotification &&
                      notif.date === selectedNotification.date
                      ? "selected"
                      : ""
                      }`}
                    onClick={() =>
                      navigate(
                        `/lassie/logbook/windfarm/${windfarm_id}/year/${selectedYear}/notification/${notif.date}`
                      )
                    }
                  >
                    <h4>
                      {toZonedDateStr(
                        new Date(notif.first_lightning_timestamp),
                        timeZone
                      )}
                    </h4>
                  </div>
                )) : null

              }
            </div>
          </div>

          {date && !selectedNotification && (
            <p className="notifications-label">
              There is no notification on {date}.
            </p>
          )}

          {selectedNotification && historicActivity && (
            <div
              key={selectedNotification.first_lightning_timestamp}
              className="notification"
            >
              <div className="date-events-container">
                <div className="date-title">
                  <p>
                    <b>
                      {toZonedDateStr(
                        new Date(selectedNotification.first_lightning_timestamp),
                        timeZone
                      )}
                    </b>
                  </p>
                  <p>
                    {toZonedDateStr(
                      new Date(selectedNotification.first_lightning_timestamp),
                      timeZone,
                      true
                    )
                      .split(" ")
                      .pop()}
                  </p>
                </div>
                <img className="arrow" src={arrowIcon} alt="arrow" />
                <div className="date-title">
                  <p>
                    <b>
                      {toZonedDateStr(
                        new Date(selectedNotification.last_lightning_timestamp),
                        timeZone
                      )}
                    </b>
                  </p>
                  <p>
                    {toZonedDateStr(
                      new Date(selectedNotification.last_lightning_timestamp),
                      timeZone,
                      true
                    )
                      .split(" ")
                      .pop()}
                  </p>
                </div>
              </div>

              {historicActivity && (
                <OlMap
                  forceShiftkeyToZoom
                  lightning_strikes={[
                    ...historicActivity.map((t) => t.turbine_lightning)
                  ].flat()}
                  turbines={historicActivity}
                />
              )}

              <h3>Team response</h3>
              {filteredActions.length > 0 ? (
                <div className="user-action">
                  <p>
                    In the following <b>three</b> weeks, there were{" "}
                    {filteredActions.length} registered user actions.
                  </p>
                  <InfoCards
                    statistics={[
                      {
                        value: filteredActions.filter(
                          (hs) => hs.action === "inspection"
                        ).length,
                        title: "Inspections",
                      },
                      {
                        value: filteredActions.filter(
                          (hs) => hs.action === "acceptable_risk"
                        ).length,
                        title: "Accepted Risk",
                      },
                      {
                        value: filteredActions.filter(
                          (hs) => hs.document_id !== null
                        ).length,
                        title: "Uploaded Documents",
                      },
                      {
                        value: filteredActions.filter((hs) => !!hs.user_comment).length,
                        title: "Comments",
                      },
                    ]}
                  />
                </div>
              ) : (
                <p>No user actions registered in the 3 weeks after the lightning event.</p>
              )}


              {historicActivity &&
                historicActivity
                  .filter((turbine) => turbine.turbine_lightning.length > 0)
                  .sort(
                    (at, bt) =>
                      bt.probability_of_atleast_one -
                      at.probability_of_atleast_one
                  )
                  .map((turbine) => {
                    // Kept as YYYY-MM-DD strings, which sort as dates. Turning them back into
                    // Dates read them as UTC midnight and showed the day before to anyone
                    // west of UTC.
                    const lightningDates = Array.from(
                      new Set(
                        turbine.turbine_lightning.map((tl) =>
                          toZonedDateStr(new Date(tl.timestamp), timeZone)
                        )
                      )
                    ).sort();
                    const turbineFirstStrikeDay = lightningDates[0];


                    return (
                      <div className="notification-turbine" key={turbine.id}>
                        <h2
                          title={
                            {
                              acceptable_risk: "Managed",
                              at_risk: "At risk",
                              marked_for_inspection: "Marked for inspection"
                            }[turbine.status]
                          }
                        >
                          <div
                            className="turbine-name-pill"
                            onClick={() => handleNameClick(turbine)}
                          >
                            <img
                              className="turbine-pill-icon"
                              src={getIconForTurbine(turbine, false)}
                              alt={turbine.name}
                            />
                            <p className="turbine-name">{turbine.name}</p>
                          </div>
                        </h2>
                        {lightningDates.map((day) => (
                          <p key={day}>{day}</p>
                        ))}

                        <TurbineLightningRuler
                          strikes={turbine.turbine_lightning}
                        />

                        <table className="scenario-table">
                          <tbody>
                            <tr>
                              <th colSpan={2}>Triggered scenarios</th>
                            </tr>
                            {historicTriggers &&
                              historicTriggers
                                .filter((ht) => ht.turbine_id === turbine.id)
                                .map((ht) => {
                                  const sc = scenarios.find(
                                    (s) => s.id === ht.scenario_id
                                  );
                                  if (!sc) return;
                                  return (
                                    <tr key={sc.id}>
                                      <td>{sc.title}</td>
                                      <td>{ht.trigger_count}</td>
                                    </tr>
                                  );
                                })}
                          </tbody>
                        </table>

                        {filteredActions
                          .filter((hs) => hs.turbine_id === turbine.id)
                          .sort(
                            (a, b) => new Date(a.target_date) - new Date(b.target_date)
                          )
                          .map((hs) => {
                            // The day as it was picked: stored as midnight UTC, so read in UTC.
                            const actionDay = toZonedDateStr(new Date(hs.target_date), null);
                            const dayDiff = Math.round(
                              (Date.parse(actionDay) - Date.parse(turbineFirstStrikeDay)) / MS_IN_DAY
                            );

                            return (
                              <div key={hs.id} className="user-action">
                                <p>
                                  {actionDay}{" "}
                                  {dayDiff !== 0 && (
                                    <>
                                      (
                                      {Math.abs(dayDiff)} day{Math.abs(dayDiff) > 1 ? "s" : ""}{" "}
                                      {dayDiff > 0 ? "later" : "earlier"})
                                    </>
                                  )}
                                </p>
                                <b>Set status {hs.action.replaceAll("_", " ")}</b>
                                {hs.document_id && (
                                  <p>
                                    <b>Document</b>
                                  </p>
                                )}
                                {!!hs.user_comment && (
                                  <p className="user-comment">"{hs.user_comment}"</p>
                                )}
                                <p>{hs.user_name}</p>
                              </div>
                            );
                          })}

                        {historicUserAction &&
                          historicUserAction.filter(
                            (hs) => hs.turbine_id === turbine.id
                          ).length === 0 && (
                            <div>
                              <p style={{ color: "darkred" }}>
                                No team reaction within the first 3 weeks.
                              </p>
                            </div>
                          )}
                      </div>
                    );
                  })}
            </div>
          )}
        </>
      )}
    </div>
  );
}